/**
 * Schema Samples Service
 *
 * Fetches pre-generated trigger output sample payloads from the Otto
 * `schemaSamples` catalog. These samples are curated from the connector's
 * swagger schemas and are used at design time to preview what a trigger
 * will emit, without invoking the real connector.
 *
 * Endpoint:
 *   GET /automationProjects/{projectName}/managedApis/{apiName}/apiOperations/{operationName}/schemaSamples/{schemaSource}
 *
 * Schema source picks:
 *   - response-200       — polling triggers (HTTP 200 response schema)
 *   - notification-content — webhook / push triggers (x-ms-notification-content)
 *
 * Returns null on any non-2xx response — callers fall back to the heuristic
 * generator in `testDataService.generateSampleFromSchema`. We do not surface
 * errors to the user; missing samples are expected (the BPM catalog covers
 * ~85% of trigger operations, leaving ~15% to the heuristic fallback).
 */

import { apiVersions, getLogicEndpoint } from './endpoints';
import { isLocalMode, localFetch, type LocalFetchOptions } from './localRuntimeClient';
import { isArmMockMode } from './armConfig';

export type SchemaSampleSource = 'response-200' | 'notification-content';

export interface FetchSchemaSampleOptions {
  projectName: string;
  apiName: string;
  operationName: string;
  schemaSource: SchemaSampleSource;
  accessToken: string;
}

/**
 * Picks the right `schemaSource` for a trigger type.
 * Mirrors the polling/webhook predicate used by `testDataService.getTriggerOutputSchema`
 * so we stay consistent with how the rest of the portal classifies triggers.
 */
export const pickSchemaSource = (triggerType: string | null | undefined): SchemaSampleSource => {
  const t = (triggerType ?? '').toLowerCase();
  return t.includes('webhook') || t.includes('notification') ? 'notification-content' : 'response-200';
};

/**
 * Fetches a pre-generated trigger output sample. Returns null when no sample
 * exists for the given combination (404), the schema source is invalid (400),
 * or any network/auth failure occurs.
 */
export const fetchTriggerSchemaSample = async (
  opts: FetchSchemaSampleOptions,
): Promise<Record<string, unknown> | null> => {
  const url =
    `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}` +
    `/managedApis/${encodeURIComponent(opts.apiName)}` +
    `/apiOperations/${encodeURIComponent(opts.operationName)}` +
    `/schemaSamples/${encodeURIComponent(opts.schemaSource)}` +
    `?api-version=${apiVersions.otto}`;

  try {
    if (isLocalMode() || isArmMockMode()) {
      return await localFetch<Record<string, unknown>>(url, { method: 'GET' } as LocalFetchOptions);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${opts.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // 404 (no sample), 400 (bad source), 401/403 (auth) — all silently
      // degrade to the heuristic fallback. Caller decides how to react to null.
      return null;
    }

    return (await response.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
};
